import { Link, useLocation } from "react-router-dom";
import { Mail } from "lucide-react";

const EMAIL = "hindiganga.learnings@gmail.com";
const UPDATED = "29 August 2026";

const pages = {
  "/terms": {
    label: "Terms & Conditions",
    title: "Simple terms for learning with Hindi Ganga.",
    intro: "These terms explain how our live classes, enrolments and payments work. By booking a class or making a payment, a parent or adult learner agrees to these terms.",
    sections: [
      ["Our service", "Hindi Ganga Online Classes provides live online tuition in Hindi, English, Maths, Science and Coding. The selected subject, lesson duration, timetable and fee are confirmed during enrolment."],
      ["Bookings and payments", "The amount, plan and billing period shown at secure checkout apply to that enrolment. Payments are processed by Razorpay. We do not store card, UPI or bank-account details. A booking is confirmed only after payment has been verified."],
      ["Parents and learners", "A parent or legal guardian must make bookings and payments for learners under 18. Please provide accurate contact, student and scheduling information, and keep the learning environment suitable for online lessons."],
      ["Attendance and rescheduling", "Please contact us at least 24 hours before a scheduled lesson if you need to reschedule. We will make reasonable efforts to offer an alternative time, subject to teacher availability. Missed or late-cancelled lessons may be treated as delivered."],
      ["Changes", "We may update lesson availability, teachers or fees from time to time. Any change to a future plan or renewal will be communicated before it takes effect."],
    ],
  },
  "/privacy": {
    label: "Privacy Policy",
    title: "Your family's information, handled with care.",
    intro: "This policy explains the limited information Hindi Ganga Online Classes collects and how we use it to arrange and deliver online tuition.",
    sections: [
      ["Information we collect", "When you contact us or enrol, we may collect a parent or adult learner's name, email address, phone number, student name, age or grade, subject preference, timetable details and messages you send us."],
      ["How we use it", "We use this information to respond to enquiries, arrange lessons, confirm enrolments, provide teaching support, communicate about schedules and meet legal or accounting obligations."],
      ["Payments", "Payments are processed by Razorpay. Hindi Ganga does not collect or store full card, UPI or bank-account details. Razorpay handles payment information under its own privacy and security practices."],
      ["Sharing and security", "We share information only with teachers and service providers who need it to deliver classes or process payments, or where required by law. We take reasonable steps to protect information, but no online service can guarantee absolute security."],
      ["Children's privacy", "For a learner under 18, a parent or legal guardian must provide the information and communicate with us about enrolment and payments."],
      ["Your choices", "You may ask to review, correct or delete personal information that we hold, subject to records we must retain by law. Contact us using the email below."],
    ],
  },
  "/refund-policy": {
    label: "Refund Policy",
    title: "A clear approach to refund requests.",
    intro: "We want families to feel confident when enrolling. This policy explains when a payment may be refunded or credited.",
    sections: [
      ["Eligible refunds", "A refund or lesson credit may be offered if Hindi Ganga cancels a paid lesson and cannot provide a suitable replacement, or if there has been a duplicate or incorrect payment."],
      ["Delivered lessons", "Fees for lessons that have already been delivered, or for lessons missed or cancelled late by the learner, are generally non-refundable."],
      ["How to request a refund", "Email us within 7 calendar days of the payment with the student name, payment date and payment reference. We may ask for additional details to review the request."],
      ["Processing", "Approved refunds are sent to the original payment method wherever possible. Banks and payment providers may take 5 to 10 business days to complete the refund after we process it."],
      ["Future charges", "If you do not wish to continue after a paid period, please follow our cancellation policy before the next payment is due."],
    ],
  },
  "/cancellation-policy": {
    label: "Cancellation Policy",
    title: "Stopping or changing a class plan.",
    intro: "We understand schedules can change. This policy covers cancelling a plan and rescheduling individual lessons.",
    sections: [
      ["Cancelling a future plan", "To stop future classes or a future renewal, email us at least 7 calendar days before the next payment date. Your classes remain available until the end of the period already paid for."],
      ["Rescheduling one lesson", "Please request changes at least 24 hours before the scheduled lesson. Rescheduling is subject to teacher availability and cannot always be guaranteed."],
      ["Late changes and missed lessons", "A lesson cancelled with less than 24 hours' notice, or a missed lesson, may be treated as delivered. We may make an exception where there is a genuine emergency."],
      ["Our cancellation of a lesson", "If we need to cancel a lesson, we will offer a reasonable alternative time. If we cannot provide one, we will offer a lesson credit or refund under our Refund Policy."],
    ],
  },
};

const links = [
  ["/terms", "Terms & Conditions"],
  ["/privacy", "Privacy Policy"],
  ["/refund-policy", "Refund Policy"],
  ["/cancellation-policy", "Cancellation Policy"],
];

export default function LegalPolicy() {
  const { pathname } = useLocation();
  const policy = pages[pathname] || pages["/terms"];

  return (
    <main className="mx-auto max-w-4xl px-6 py-16 lg:px-10 lg:py-24" data-testid="legal-policy-page">
      <div className="rounded-[2rem] border border-border bg-sand px-6 py-10 card-shadow sm:px-10 sm:py-14">
        <p className="text-xs font-medium uppercase tracking-[0.28em] text-saffron">{policy.label}</p>
        <h1 className="mt-4 max-w-2xl font-serif text-4xl font-semibold leading-[1.05] tracking-tight text-ink sm:text-5xl">{policy.title}</h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">{policy.intro}</p>
        <p className="mt-5 text-xs uppercase tracking-[0.14em] text-muted-foreground">Last updated: {UPDATED}</p>
      </div>

      <div className="mt-10 space-y-8">
        {policy.sections.map(([heading, body]) => (
          <section key={heading} className="rounded-3xl border border-border bg-white p-7 card-shadow sm:p-9">
            <h2 className="font-serif text-2xl font-semibold tracking-tight text-teal-dark">{heading}</h2>
            <p className="mt-3 text-sm leading-7 text-muted-foreground sm:text-base">{body}</p>
          </section>
        ))}
      </div>

      <div className="mt-10 rounded-3xl border border-saffron/25 bg-saffron-soft p-7 sm:p-8">
        <h2 className="font-serif text-2xl font-semibold text-ink">Questions about a policy?</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Contact us before making a payment and we will be happy to help.</p>
        <a href={"mailto:" + EMAIL} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-saffron-dark hover:text-saffron">
          <Mail className="h-4 w-4" /> {EMAIL}
        </a>
      </div>

      <nav className="mt-10 flex flex-wrap gap-x-6 gap-y-3" aria-label="Policies">
        {links.filter(([to]) => to !== pathname).map(([to, label]) => (
          <Link key={to} to={to} className="text-sm font-medium text-teal hover:text-saffron">{label}</Link>
        ))}
      </nav>
    </main>
  );
}
